// @ts-check
import { uid } from '@/utils/uid'
/**
 * @typedef {'success' | 'error' | 'info' | 'warning'} AlertVariant
 */

/**
 * @typedef {{
 *   id: string,
 *   message: string,
 *   title: string,
 *   variant: AlertVariant,
 *   timeout?: number,
 *   teleportTo?: string
 * }} Alert
 */

/**
 * @typedef {Required<Pick<import('./useAlert').Alert, 'message' | 'title' | 'variant' | 'teleportTo'> & Pick<import('./useAlert').Alert, 'timeout'>>} AlertOptions
 */

/**
 * @param {import('vue').Ref<Alert[]>} alertsRef
 * @param {{ defaultTimeout?: number }} [options]
 */
export const useAlert = (alertsRef, { defaultTimeout = 4000 } = {}) => {
  /** @type {Map<string, number>} */
  const timeoutMap = new Map()
  /** @type {Map<string, () => void>} */
  const resolverMap = new Map()

  /**
   * @param {AlertOptions} alert
   * @returns {Promise<void>}
   */
  function push(alert) {
    return new Promise((resolve) => {
      const id = uid('alert')
      const timeout = alert.timeout ?? defaultTimeout
      /** @type {Alert} */
      const item = { id, timeout, ...alert }

      alertsRef.value.push(item)
      resolverMap.set(id, resolve)

      if (timeout > 0) {
        const timerId = window.setTimeout(() => remove(id), timeout)
        timeoutMap.set(id, timerId)
      }
    })
  }

  /**
   * @param {string} id
   */
  function remove(id) {
    const idx = alertsRef.value.findIndex((a) => a.id === id)
    if (idx !== -1) alertsRef.value.splice(idx, 1)

    if (timeoutMap.has(id)) {
      clearTimeout(timeoutMap.get(id))
      timeoutMap.delete(id)
    }

    if (resolverMap.has(id)) {
      const resolve = resolverMap.get(id)
      resolve()
      resolverMap.delete(id)
    }
  }

  return {
    push,
    remove,
    /**
     * 성공 알림
     * @param {{ message: string, title?: string, teleportTo?: string }} option
     * @returns {Promise<void>}
     */
    success: ({ message, title = '성공', teleportTo }) =>
      push({ message, title, variant: 'success', timeout: defaultTimeout, teleportTo }),

    /**
     * 오류 알림
     * @param {{ message: string, title?: string, teleportTo?: string }} option
     * @returns {Promise<void>}
     */
    error: ({ message, title = '오류', teleportTo }) =>
      push({ message, title, variant: 'error', timeout: defaultTimeout, teleportTo }),

    /**
     * 정보 알림
     * @param {{ message: string, title?: string, teleportTo?: string }} option
     * @returns {Promise<void>}
     */
    info: ({ message, title = '알림', teleportTo }) =>
      push({ message, title, variant: 'info', timeout: defaultTimeout, teleportTo }),

    /**
     * 경고 알림
     * @param {{ message: string, title?: string, teleportTo?: string }} option
     * @returns {Promise<void>}
     */
    warning: ({ message, title = '주의', teleportTo }) =>
      push({ message, title, variant: 'warning', timeout: defaultTimeout, teleportTo })
  }
}
